import * as React from 'react';
import { addClass } from '../../utils/css.js';
import { useMergedRefs } from '../../utils/component-util.js';
import { containsNumericValue, warnOnce } from '../../utils/common.js';
import { useId } from '../../utils/hooks/useId.js';
import { ApplicationTheme, useTheme, ThemeProvider } from '../ThemeProvider/ThemeProvider.js';
import './Application.scss.js';

const ApplicationContent = React.forwardRef((props, ref) => {
  const { htmlId, theme = ApplicationTheme.Light, dataTa, className, children, ...rest } = props;
  const ThemeContext = useTheme();
  const id = useId(htmlId, ["application"]);
  React.useEffect(() => {
    if (theme !== ThemeContext.themeClass) {
      ThemeContext.changeTheme(theme === ApplicationTheme.Dark ? "dark" : "light");
    }
  }, [theme, ThemeContext]);
  const classes = addClass("vai-application", ThemeContext.themeClass, className);
  return /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    "data-ta": dataTa,
    ref,
    className: classes,
    ...rest
  }, children);
});
const Application = React.forwardRef((props, ref) => {
  const { htmlId, theme = ApplicationTheme.Light, dataTa, className, children, ...rest } = props;
  const localRef = React.useRef();
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development" && localRef.current && window.document && window.document.body) {
      try {
        const { margin, padding } = window.getComputedStyle(window.document.body);
        if (containsNumericValue(padding) || containsNumericValue(margin)) {
          warnOnce(`Application component expected body to have no margin nor padding. 
Instead got 'margin: ${margin}', and 'padding: ${padding}'`.trim());
        }
      } catch (e) {
        console.error("Failed when defining if body has extra margin:", e?.message);
      }
    }
  }, [localRef]);
  return /* @__PURE__ */ React.createElement(ThemeProvider, null, /* @__PURE__ */ React.createElement(ApplicationContent, {
    htmlId,
    ref: useMergedRefs(localRef, ref),
    theme,
    dataTa,
    className,
    ...rest
  }, children));
});

export { Application };
